'use client'

import { useEffect, useState, useRef } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedCounterProps {
  value: number
  className?: string
  duration?: number // Animation duration in ms
}

/**
 * Animated counter with slot-machine style animation.
 * Old number slides down and fades out, new number slides in from top.
 */
export function AnimatedCounter({
  value,
  className,
  duration = 400
}: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(value)
  const [previousValue, setPreviousValue] = useState<number | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const prevValueRef = useRef(value)

  useEffect(() => {
    if (prevValueRef.current !== value) {
      // Store the old value for animation
      setPreviousValue(prevValueRef.current)
      setIsAnimating(true)

      // After a brief moment, update to new value
      const updateTimer = setTimeout(() => {
        setDisplayValue(value)
        prevValueRef.current = value
      }, duration / 3)

      // Clear animation state
      const resetTimer = setTimeout(() => {
        setIsAnimating(false)
        setPreviousValue(null)
      }, duration)

      return () => {
        clearTimeout(updateTimer)
        clearTimeout(resetTimer)
      }
    }
  }, [value, duration])

  return (
    <span
      className={cn('relative inline-flex overflow-hidden', className)}
      style={{ height: '1.2em', lineHeight: '1.2em' }}
    >
      {/* Old value sliding down */}
      {isAnimating && previousValue !== null && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          style={{
            animation: `slideDown ${duration}ms ease-out forwards`
          }}
        >
          {previousValue}
        </span>
      )}

      {/* Current/new value sliding in from top */}
      <span
        className={cn(
          'flex items-center justify-center',
          isAnimating && 'animate-slideIn'
        )}
        style={
          isAnimating
            ? {
                animation: `slideIn ${duration}ms ease-out forwards`
              }
            : undefined
        }
      >
        {displayValue}
      </span>

      <style jsx>{`
        @keyframes slideDown {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        @keyframes slideIn {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </span>
  )
}
